'use client';

import { useState, type FormEvent } from 'react';
import { Button } from '@open-design/components';
import { useAuth } from './AuthContext';
import { MIN_LENGTH, translatePasswordRefusal } from './password-errors';
import styles from './NewPasswordView.module.css';
import { Crie3Wordmark } from '../components/Crie3Wordmark';

// Hosted-Brazil surface: copy is pt-BR, matching LoginView and AccountPanel. If
// Crie3 later internationalizes, these move behind the i18n Dict together.

/**
 * Supabase answers in English. The refusals that mean the same thing wherever a
 * password is written live in `password-errors`, shared with `AccountPanel`;
 * the one below does not, because a dead credential here means the emailed link
 * went stale, not that a live session expired. Anything unrecognised falls
 * through verbatim rather than being flattened into a generic message that
 * hides what went wrong.
 */
function translate(message: string): string {
  const shared = translatePasswordRefusal(message);
  if (shared) return shared;
  if (/session|expired|invalid|jwt/i.test(message)) {
    return 'Seu link de redefinição expirou. Peça um novo na tela de login.';
  }
  return message;
}

/**
 * The last step of "esqueci minha senha", and the reason the flow is a loop
 * without it.
 *
 * Opening a recovery link signs the visitor in — Supabase grants a session so
 * the password can be changed. Without a screen here, that session was the end
 * of the road: the app opened, nothing asked for a new password, and the next
 * visit locked them out again with the same forgotten one. So this renders
 * *instead of* the app while `passwordRecovery` holds, and the only ways past
 * it are writing a new password or signing out.
 */
export function NewPasswordView() {
  const { user, updatePassword, signOut } = useAuth();
  const [password, setPassword] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const email = user?.email ?? '';

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (busy) return;
    setError(null);

    // Checked here rather than left to Supabase: the mismatch is a typo the
    // person can fix without a round-trip, and a confirmed typo silently saved
    // would lock them out exactly like the bug this screen closes.
    if (password !== confirmation) {
      setError('As duas senhas não são iguais.');
      return;
    }

    setBusy(true);
    const { error: failure } = await updatePassword(password);
    setBusy(false);
    if (failure) {
      setError(translate(failure));
      return;
    }
    // Success needs no screen: `updatePassword` clears the recovery state, and
    // the gate swaps this view for the app on the next render.
  }

  return (
    <div className={styles.root}>
      <div className={styles.card}>
        <div className={styles.brand}>
                    <Crie3Wordmark className={styles.brandName} height="32px" />
        </div>

        <h1 className={styles.headline}>Defina uma nova senha.</h1>
        <p className={styles.tagline}>
          {email ? (
            <>
              Esta será a senha de <strong>{email}</strong> daqui em diante.
            </>
          ) : (
            <>Escolha a senha que você vai usar para entrar daqui em diante.</>
          )}
        </p>

        <form onSubmit={submit}>
          <div className={styles.fields}>
            <input
              type="password"
              className={styles.field}
              placeholder={`Nova senha (mínimo ${MIN_LENGTH} caracteres)`}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="new-password"
              autoFocus
              required
              minLength={MIN_LENGTH}
              disabled={busy}
            />
            <input
              type="password"
              className={styles.field}
              placeholder="Repita a senha"
              value={confirmation}
              onChange={(e) => setConfirmation(e.target.value)}
              autoComplete="new-password"
              required
              minLength={MIN_LENGTH}
              disabled={busy}
            />
          </div>

          {error ? (
            <p className={styles.error} role="alert">
              {error}
            </p>
          ) : null}

          <Button type="submit" variant="primary" className={styles.submit} disabled={busy}>
            {busy ? '…' : 'Salvar nova senha'}
          </Button>
        </form>

        {/* The escape hatch. Someone who opened the link on the wrong account,
            or changed their mind, must not be trapped on a screen the app sits
            behind. */}
        <p className={styles.switch}>
          Não é você?{' '}
          <Button variant="ghost" onClick={() => void signOut()} disabled={busy}>
            Sair
          </Button>
        </p>
      </div>
    </div>
  );
}
